/**
 * The announcement bar's rotator.
 *
 * Two or more promises share one line, one at a time. The markup is already a
 * complete list before this runs — CSS shows the first item — so with scripts
 * blocked the bar is a correct, static, one-line bar rather than an empty one.
 *
 * What this adds is the hand-over, and three things that stop an auto-advancing
 * strip being a nuisance (WCAG 2.2.2, "Pause, Stop, Hide"):
 *
 * - it pauses while the pointer is over the bar or a link inside it has focus,
 *   so a message can never slide out from under someone reading or clicking it;
 * - it stops entirely while the tab is in the background, so a tab left open
 *   for an hour is not still cycling;
 * - it does not start at all under `prefers-reduced-motion`, which leaves the
 *   first message showing for good.
 *
 * Messages that are not current are `inert` and `aria-hidden`, so a screen
 * reader and the tab order see exactly the one line that is on screen.
 */
if (!customElements.get('airflux-announce')) {
  customElements.define(
    'airflux-announce',
    class AirfluxAnnounce extends HTMLElement {
      connectedCallback() {
        this.items = [...this.querySelectorAll('[data-announce-item]')];
        if (this.items.length < 2) return;

        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.motionQuery.matches) return;

        this.index = 0;
        this.interval = Math.max(3000, Number(this.getAttribute('interval')) || 6000);
        this.show(0);

        this.onEnter = () => this.stop();
        this.onLeave = () => this.start();
        this.onVisibility = () => (document.hidden ? this.stop() : this.start());

        this.addEventListener('pointerenter', this.onEnter);
        this.addEventListener('pointerleave', this.onLeave);
        this.addEventListener('focusin', this.onEnter);
        this.addEventListener('focusout', this.onLeave);
        document.addEventListener('visibilitychange', this.onVisibility);

        this.start();
      }

      disconnectedCallback() {
        this.stop();
        this.removeEventListener('pointerenter', this.onEnter);
        this.removeEventListener('pointerleave', this.onLeave);
        this.removeEventListener('focusin', this.onEnter);
        this.removeEventListener('focusout', this.onLeave);
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      start() {
        if (this.timer || document.hidden || this.matches(':hover, :focus-within')) return;
        this.timer = setInterval(() => this.show((this.index + 1) % this.items.length), this.interval);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }

      show(next) {
        this.index = next;
        this.items.forEach((item, i) => {
          const current = i === next;
          item.toggleAttribute('data-announce-current', current);
          item.inert = !current;
          item.setAttribute('aria-hidden', String(!current));
        });
      }
    }
  );
}
